<template>
  <WebsiteWrapper @selected="selectWebsite" :verified="2" :plan="0" mandatory>
    <v-card class="tw-mb-6 mx-auto">
      <v-container class="sr-over-container">
        <span class="sr-title">
          {{ $t(`menu.company.right.title`) }}
        </span>

        <v-card-text>
          <div class="tw-mt-2">
            <canvas :id="id" class="sr-statistics-chart"></canvas>
          </div>

          <div class="tw-flex tw-justify-between tw-mt-4">
            <v-btn :disabled="!selectedWebsite" text x-small @click="previousChart">
              <v-icon left x-small>fa-angle-left</v-icon>
              <span>{{ $t("common.back") }}</span>
            </v-btn>
            <span class="tw-font-semibold">{{ chartName }}</span>
            <v-btn :disabled="!selectedWebsite" text x-small @click="nextChart">
              <span>{{ $t("common.next") }}</span>
              <v-icon right x-small>fa-angle-right</v-icon>
            </v-btn>
          </div>
        </v-card-text>
      </v-container>
    </v-card>
  </WebsiteWrapper>
</template>

<script>
import { groupBy, orderBy, range } from "lodash"
import { Chart } from "chart.js"
import ChartjsPluginZoom from "chartjs-plugin-zoom"
import WebsiteWrapper from "../../common/Form/WebsiteWrapper"

export default {
  name: "StatisticsChart",
  components: {
    WebsiteWrapper,
  },
  props: {
    data: { type: Array },
  },
  data: () => ({
    charts: [
      "raw",
      //"progression",
      //"likes_progression",
      //"prints_progression",
      //"publications_progression",
    ],
    chart: 0,
    statistics: [],
    websitesList: [],
    selectedWebsite: "",
    defaultChunk: {
      id: null,
      calculation: false,
      database: null,
      date_interval: "1D",
      date_tick: null,
      likes: 0,
      prints: 0,
      publications_media: 0,
      publications_question: 0,
      publications_response: 0,
      publications_review: 0,
      registrations: 0,
      sharing_email: 0,
      sharing_facebook: 0,
      sharing_linkedin: 0,
      sharing_twitter: 0,
      sharing_whatsapp: 0,
      subject_id: null,
      subject_type: null,
    },
  }),
  watch: {
    chart() {
      this.generateChart()
    },
  },
  created() {
    this.id = Math.random()
      .toString(36)
      .substring(2, 15)
  },
  async mounted() {
    this.generateMonthsArray()
    this.generateColorsArray()

    this.context = document.getElementById(this.id)

    Chart.plugins.register(ChartjsPluginZoom)
  },
  computed: {
    chartName() {
      return this.$t(`charts.statistics.names.${this.charts[this.chart]}`)
    },
  },
  methods: {
    selectWebsite(website) {
      this.selectedWebsite = website.id
      this.fetchStatistics(website.host)
    },
    fetchStatistics(host) {
      this.$axios
        .get("/office/manager/companies/statistics/domain", {
          params: { url: `https://${host}` },
        })
        .then(response => {
          this.statistics = response.data
          this.generateChart()
        })
    },
    generateMonthsArray() {
      // Date label
      this.monthsLabels = [
        this.$t("common.months.january"),
        this.$t("common.months.february"),
        this.$t("common.months.march"),
        this.$t("common.months.april"),
        this.$t("common.months.may"),
        this.$t("common.months.june"),
        this.$t("common.months.july"),
        this.$t("common.months.august"),
        this.$t("common.months.september"),
        this.$t("common.months.october"),
        this.$t("common.months.november"),
        this.$t("common.months.december"),
      ]

      const currentDate = new Date()
      const thirdMonth = currentDate.getMonth()
      this.thirdMonth = thirdMonth

      currentDate.setMonth(currentDate.getMonth() - 1)
      const secondMonth = currentDate.getMonth()
      this.secondMonth = secondMonth

      currentDate.setMonth(currentDate.getMonth() - 1)
      const firstMonth = currentDate.getMonth()
      this.firstMonth = firstMonth

      this.months = [this.monthsLabels[firstMonth], this.monthsLabels[secondMonth], this.monthsLabels[thirdMonth]]
    },
    generateColorsArray() {
      // Colors
      this.likesColoration = this.generateColors(255, 99, 132)
      this.printsColoration = this.generateColors(255, 206, 86)
      this.publicationsColoration = this.generateColors(75, 192, 192)

      this.monthsColorLabels = [
        this.generateColors("255", "0", "127"), // January
        this.generateColors("0", "0", "255"), // February
        this.generateColors("255", "127", "0"), // March
        this.generateColors("127", "0", "255"), // April
        this.generateColors("0", "255", "255"), // May
        this.generateColors("255", "255", "0"), // June
        this.generateColors("0", "255", "127"), // July
        this.generateColors("255", "0", "255"), // August
        this.generateColors("127", "255", "0"), // September
        this.generateColors("255", "0", "0"), // October
        this.generateColors("0", "127", "255"), // November
        this.generateColors("0", "255", "0"), // December
      ]

      this.monthsColor = [
        this.monthsColorLabels[this.firstMonth],
        this.monthsColorLabels[this.secondMonth],
        this.monthsColorLabels[this.thirdMonth],
      ]
    },
    generateColors(r, g, b) {
      return {
        pointBackgroundColor: `rgba(${r}, ${g}, ${b}, 0.4)`,
        pointBorderColor: `rgba(${r}, ${g}, ${b}, 0.4)`,
        backgroundColor: `rgba(${r}, ${g}, ${b}, 0)`,
        borderColor: `rgba(${r}, ${g}, ${b}, 1)`,
        borderWidth: 1,
      }
    },
    // To make a progression, it sum value with total at every iteration
    summer(sum, index) {
      return value => (sum += value[index])
    },
    orderByDate() {
      return orderBy(this.statistics, ["date_tick"], "asc")
    },
    groupByDate() {
      return groupBy(this.statistics, function(item) {
        return item.date_tick.substring(0, 10)
      })
    },
    groupByMonth(data) {
      return groupBy(data, item => {
        const key = Object.keys(item)[0]
        // We need to adjust the index : january = 0, february = 1, march = 2, etc.
        return Number(key.substring(5, 7) - 1)
      })
    },
    reduceGroups(groups, index) {
      return Object.keys(groups).map(date => {
        return {
          [date]: groups[date].reduce((accumulator, statistic) => accumulator[index] + statistic[index]),
        }
      })
    },
    daysInMonth(month) {
      const year = new Date().getFullYear()
      const date = new Date(year, month, 0)
      return { days: date.getDate(), date }
    },
    generateChunk(chunks, month) {
      if (!chunks.hasOwnProperty(month)) {
        const block = this.daysInMonth(month)
        chunks[month] = []

        for (let i = 1; i < block.days; i++) {
          const formatDate = `${block.date.getFullYear()}-${String(block.date.getMonth()).padStart(2, "0")}-${String(
            i
          ).padStart(2, "0")}`
          let chunk = {}
          chunk[formatDate] = this.defaultChunk
          chunk[formatDate].date_tick = formatDate
          chunks[month].push(chunk)
        }
      }
      return chunks
    },
    fillChunks(chunks) {
      chunks = this.generateChunk(chunks, this.firstMonth)
      chunks = this.generateChunk(chunks, this.secondMonth)
      chunks = this.generateChunk(chunks, this.thirdMonth)

      return chunks
    },
    extractLabels(reduced) {
      return reduced.map(item => Object.keys(item)[0])
    },
    extractData(reduced) {
      return reduced.map(item => Object.values(item)[0])
    },
    rawData(dataset) {
      this.likesLabels = dataset.likesLabels
      this.likesData = dataset.likesData.map(data => data.likes)

      this.printsLabels = dataset.printsLabels
      this.printsData = dataset.printsData.map(data => data.prints)

      this.publicationsLabels = dataset.publicationsLabels
      this.publicationsData = dataset.publicationsData.map(data => data.publications_review)
    },
    progressionData(dataset) {
      this.likesLabels = dataset.likesLabels
      this.likesData = dataset.likesData.map(this.summer(0, "likes"))

      this.printsLabels = dataset.printsLabels
      this.printsData = dataset.printsData.map(this.summer(0, "prints"))

      this.publicationsLabels = dataset.publicationsLabels
      this.publicationsData = dataset.publicationsData.map(this.summer(0, "publications_review"))
    },
    likesProgressionData(dataset) {
      let chunks = this.groupByMonth(dataset.reducedLikes)
      chunks = this.fillChunks(chunks)

      this.likesFirstMonthLabel = this.months[0]
      this.likesFirstMonthData = this.extractData(chunks[this.firstMonth]).map(this.summer(0, "likes"))
      this.likesFirstMonthColoration = this.monthsColor[0]

      this.likesSecondMonthLabel = this.months[1]
      this.likesSecondMonthData = this.extractData(chunks[this.secondMonth]).map(this.summer(0, "likes"))
      this.likesSecondMonthColoration = this.monthsColor[1]

      this.likesThirdMonthLabel = this.months[2]
      this.likesThirdMonthData = this.extractData(chunks[this.thirdMonth]).map(this.summer(0, "likes"))
      this.likesThirdMonthColoration = this.monthsColor[2]
    },
    printsProgressionData(dataset) {
      let chunks = this.groupByMonth(dataset.reducedPrints)
      chunks = this.fillChunks(chunks)

      this.printsFirstMonthLabel = this.months[0]
      this.printsFirstMonthData = this.extractData(chunks[this.firstMonth]).map(this.summer(0, "prints"))
      this.printsFirstMonthColoration = this.monthsColor[0]

      this.printsSecondMonthLabel = this.months[1]
      this.printsSecondMonthData = this.extractData(chunks[this.secondMonth]).map(this.summer(0, "prints"))
      this.printsSecondMonthColoration = this.monthsColor[1]

      this.printsThirdMonthLabel = this.months[2]
      this.printsThirdMonthData = this.extractData(chunks[this.thirdMonth]).map(this.summer(0, "prints"))
      this.printsThirdMonthColoration = this.monthsColor[2]
    },
    publicationsProgressionData(dataset) {
      let chunks = this.groupByMonth(dataset.reducedPublications)
      chunks = this.fillChunks(chunks)

      this.publicationsFirstMonthLabel = this.months[0]
      this.publicationsFirstMonthData = this.extractData(chunks[this.firstMonth]).map(
        this.summer(0, "publications_review")
      )
      this.publicationsFirstMonthColoration = this.monthsColor[0]

      this.publicationsSecondMonthLabel = this.months[1]
      this.publicationsSecondMonthData = this.extractData(chunks[this.secondMonth]).map(
        this.summer(0, "publications_review")
      )
      this.publicationsSecondMonthColoration = this.monthsColor[1]

      this.publicationsThirdMonthLabel = this.months[2]
      this.publicationsThirdMonthData = this.extractData(chunks[this.thirdMonth]).map(
        this.summer(0, "publications_review")
      )
      this.publicationsThirdMonthColoration = this.monthsColor[2]
    },
    formatData() {
      if (this.statistics.length > 0) {
        this.statistics = this.orderByDate()
        let groups = this.groupByDate()

        const dataset = {}
        dataset.groups = groups

        const reducedLikes = this.reduceGroups(groups, "likes")
        dataset.reducedLikes = reducedLikes
        dataset.likesLabels = this.extractLabels(reducedLikes)
        dataset.likesData = this.extractData(reducedLikes)

        const reducedPrints = this.reduceGroups(groups, "prints")
        dataset.reducedPrints = reducedPrints
        dataset.printsLabels = this.extractLabels(reducedPrints)
        dataset.printsData = this.extractData(reducedPrints)

        const reducedPublications = this.reduceGroups(groups, "publications_review")
        dataset.reducedPublications = reducedPublications
        dataset.publicationsLabels = this.extractLabels(reducedPublications)
        dataset.publicationsData = this.extractData(reducedPublications)

        switch (this.charts[this.chart]) {
          case "raw":
            this.rawData(dataset)
            break
          case "progression":
            this.progressionData(dataset)
            break
          case "likes_progression":
            this.likesProgressionData(dataset)
            break
          case "prints_progression":
            this.printsProgressionData(dataset)
            break
          case "publications_progression":
            this.publicationsProgressionData(dataset)
            break
        }
      }
    },
    previousChart() {
      if (typeof this.charts[this.chart - 1] === "undefined") {
        this.chart = this.charts.length - 1
      } else {
        this.chart -= 1
      }
    },
    nextChart() {
      if (typeof this.charts[this.chart + 1] === "undefined") {
        this.chart = 0
      } else {
        this.chart += 1
      }
    },
    generateChart() {
      // Need to destroy the chart instance to clear the canvas when is already created
      if (this.StatisticChart) {
        this.StatisticChart.destroy()
      }

      this.formatData()

      switch (this.charts[this.chart]) {
        case "raw":
          this.rawChart()
          break
        case "progression":
          this.progressionChart()
          break
        case "likes_progression":
          this.likesProgressionChart()
          break
        case "prints_progression":
          this.printsProgressionChart()
          break
        case "publications_progression":
          this.publicationsProgressionChart()
          break
        case "months_color":
          this.monthsColorChart()
          break
      }
    },
    rawChartOptions() {
      return {
        scales: {
          xAxes: [
            {
              type: "time",
              time: {
                parser: "YYYY-MM-DD",
                minUnit: "day",
              },
            },
          ],
          yAxes: [
            {
              type: "logarithmic",
              ticks: {
                callback: function(value) {
                  return value.toFixed(0)
                },
                fontSize: 12,
                lineHeight: 2,
                beginAtZero: true,
                maxTicksLimit: 6,
              },
            },
          ],
        },
        plugins: {
          zoom: {
            pan: {
              enabled: true,
              mode: "xy",
            },
            zoom: {
              enabled: true,
              mode: "x",
            },
          },
        },
      }
    },
    rawChart() {
      this.StatisticChart = new Chart(this.context, {
        type: "line",
        data: {
          labels: this.likesLabels,
          datasets: [
            {
              lineTension: 0,
              label: this.$t("charts.statistics.likes"),
              data: this.likesData,
              ...this.likesColoration,
            },
            {
              lineTension: 0,
              label: this.$t("charts.statistics.prints"),
              data: this.printsData,
              ...this.printsColoration,
            },
            {
              lineTension: 0,
              label: this.$t("charts.statistics.publications"),
              data: this.publicationsData,
              ...this.publicationsColoration,
            },
          ],
        },
        options: this.rawChartOptions(),
      })
    },
    progressionChartOptions() {
      return {
        scales: {
          xAxes: [
            {
              type: "time",
              time: {
                parser: "YYYY-MM-DD",
                minUnit: "day",
              },
            },
          ],
          yAxes: [
            {
              type: "linear",
              ticks: {
                callback: function(value) {
                  return value.toFixed(0)
                },
                fontSize: 12,
                lineHeight: 2,
                beginAtZero: true,
                maxTicksLimit: 6,
              },
            },
          ],
        },
        plugins: {
          zoom: {
            pan: {
              enabled: true,
              mode: "xy",
            },
            zoom: {
              enabled: true,
              mode: "x",
            },
          },
        },
      }
    },
    progressionChart() {
      this.StatisticChart = new Chart(this.context, {
        type: "line",
        data: {
          labels: this.likesLabels,
          datasets: [
            {
              lineTension: 0,
              label: this.$t("charts.statistics.likes"),
              data: this.likesData,
              ...this.likesColoration,
            },
            {
              lineTension: 0,
              label: this.$t("charts.statistics.prints"),
              data: this.printsData,
              ...this.printsColoration,
            },
            {
              lineTension: 0,
              label: this.$t("charts.statistics.publications"),
              data: this.publicationsData,
              ...this.publicationsColoration,
            },
          ],
        },
        options: this.progressionChartOptions(),
      })
    },
    monthsProgressionChartOptions() {
      return {
        scales: {
          yAxes: [
            {
              type: "linear",
              ticks: {
                callback: function(value) {
                  return value.toFixed(0)
                },
                fontSize: 12,
                lineHeight: 2,
                beginAtZero: true,
                maxTicksLimit: 6,
              },
            },
          ],
        },
        plugins: {
          zoom: {
            pan: {
              enabled: true,
              mode: "xy",
            },
            zoom: {
              enabled: true,
              mode: "x",
            },
          },
        },
      }
    },
    likesProgressionChart() {
      this.StatisticChart = new Chart(this.context, {
        type: "line",
        data: {
          labels: range(1, 32),
          datasets: [
            {
              lineTension: 0,
              label: this.likesFirstMonthLabel,
              data: this.likesFirstMonthData,
              ...this.likesFirstMonthColoration,
            },
            {
              lineTension: 0,
              label: this.likesSecondMonthLabel,
              data: this.likesSecondMonthData,
              ...this.likesSecondMonthColoration,
            },
            {
              lineTension: 0,
              label: this.likesThirdMonthLabel,
              data: this.likesThirdMonthData,
              ...this.likesThirdMonthColoration,
            },
          ],
        },
        options: this.monthsProgressionChartOptions(),
      })
    },
    printsProgressionChart() {
      this.StatisticChart = new Chart(this.context, {
        type: "line",
        data: {
          labels: range(1, 32),
          datasets: [
            {
              lineTension: 0,
              label: this.printsFirstMonthLabel,
              data: this.printsFirstMonthData,
              ...this.printsFirstMonthColoration,
            },
            {
              lineTension: 0,
              label: this.printsSecondMonthLabel,
              data: this.printsSecondMonthData,
              ...this.printsSecondMonthColoration,
            },
            {
              lineTension: 0,
              label: this.printsThirdMonthLabel,
              data: this.printsThirdMonthData,
              ...this.printsThirdMonthColoration,
            },
          ],
        },
        options: this.monthsProgressionChartOptions(),
      })
    },
    publicationsProgressionChart() {
      this.StatisticChart = new Chart(this.context, {
        type: "line",
        data: {
          labels: range(1, 32),
          datasets: [
            {
              lineTension: 0,
              label: this.publicationsFirstMonthLabel,
              data: this.publicationsFirstMonthData,
              ...this.publicationsFirstMonthColoration,
            },
            {
              lineTension: 0,
              label: this.publicationsSecondMonthLabel,
              data: this.publicationsSecondMonthData,
              ...this.publicationsSecondMonthColoration,
            },
            {
              lineTension: 0,
              label: this.publicationsThirdMonthLabel,
              data: this.publicationsThirdMonthData,
              ...this.publicationsThirdMonthColoration,
            },
          ],
        },
        options: this.monthsProgressionChartOptions(),
      })
    },
    monthsColorChart() {
      let datasets = []

      range(0, 12).forEach(i => {
        datasets.push({
          label: this.monthsLabels[i],
          data: [
            i,
            i,
            i,
            i,
            i,
            i,
            i,
            i,
            i,
            i,
            i,
            i,
            i,
            i,
            i,
            i,
            i,
            i,
            i,
            i,
            i,
            i,
            i,
            i,
            i,
            i,
            i,
            i,
            i,
            i,
            i,
            i,
            i,
            i,
            i,
            i,
            i,
            i,
            i,
            i,
            i,
            i,
            i,
            i,
            i,
            i,
          ],
          ...this.monthsColorLabels[i],
        })
      })

      this.StatisticChart = new Chart(this.context, {
        type: "line",
        data: {
          labels: range(1, 32),
          datasets,
        },
      })
    },
  },
}
</script>
